import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  DataError,
  LoadingIndicator,
  Table,
  TableDataCell,
  TableHeadCell,
  Typography
} from "@digital-services/ui";

import { OrganizationExternalId } from "@organization/models/OrganizationExternalId";

interface Props {
  data?: OrganizationExternalId[];
  isLoading?: boolean;
  error?: Error;
  isParent?: boolean;
}

export const OrganizationExternalIds: FC<Props> = props => {
  const { data, isLoading, error, isParent } = props;

  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.external-systems"
  });

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="organization-external-systems"
    >
      <Typography variant="sectionTitle">
        {isParent
          ? t("section-title.label-for-parent")
          : t("section-title.label")}
      </Typography>

      <Table className="w-full">
        <thead>
          <tr className="leading-[0px]">
            <TableHeadCell>{t("title.label")}</TableHeadCell>
            <TableHeadCell>{t("value.label")}</TableHeadCell>
          </tr>
        </thead>
        <tbody>
          {!data && (
            <tr key="no-external-ids-available">
              <TableDataCell>{t("not-available.message")}</TableDataCell>
            </tr>
          )}
          {data?.map(({ key, value }) => {
            return (
              <tr key={`external-id-${key}`}>
                <TableDataCell>{t(key)}</TableDataCell>
                <TableDataCell>{value}</TableDataCell>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
